const LAST_POS_KEY = "last_position";

function getToken() {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
}

export function setToken(token, remember) {
  if (!token) return;

  if (remember) {
    localStorage.setItem("token", token);
    sessionStorage.removeItem("token");
  } else {
    sessionStorage.setItem("token", token);
    localStorage.removeItem("token");
  }
}

export function clearToken() {
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
}

function normalizeAccountStatus(rawUser) {
  const user = rawUser || {};
  const direct = user.account_status ?? user.accountStatus ?? user.status ?? null;

  if (
    direct &&
    ["active", "registered", "suspended", "stopped"].includes(String(direct))
  ) {
    return String(direct);
  }

  const isActive = user.is_active ?? user.isActive ?? null;

  if (
    isActive &&
    ["active", "registered", "suspended", "stopped"].includes(String(isActive))
  ) {
    return String(isActive);
  }

  if (isActive === 1 || isActive === true) return "active";
  if (isActive === 0 || isActive === false) return "registered";

  return null;
}

export function shouldRememberSession() {
  const hasLocalToken = !!localStorage.getItem("token");
  const hasSessionToken = !!sessionStorage.getItem("token");

  if (hasLocalToken && !hasSessionToken) return true;
  if (hasSessionToken && !hasLocalToken) return false;

  const hasLocalUser = !!localStorage.getItem("user");
  const hasSessionUser = !!sessionStorage.getItem("user");

  if (hasLocalUser && !hasSessionUser) return true;
  if (hasSessionUser && !hasLocalUser) return false;

  return hasLocalToken;
}

export function getStoredUser() {
  try {
    return (
      JSON.parse(localStorage.getItem("user") || "null") ||
      JSON.parse(sessionStorage.getItem("user") || "null")
    );
  } catch {
    return null;
  }
}

export function setStoredUser(user, remember = true) {
  if (!user) return;

  const accountStatus = normalizeAccountStatus(user);

  const safeUser = {
    id: user.id,
    username: user.username,
    role: user.role,
    name: user.name,
    email: user.email,
    phone: user.phone,
    gender: user.gender,
    account_status: accountStatus || user.account_status,
    is_active:
      user.is_active ??
      user.isActive ??
      user.account_status ??
      user.accountStatus ??
      user.status,
    subscription_status: user.subscription_status,
    subscription_until: user.subscription_until,
    trial_started_at: user.trial_started_at,
    subscription_expires_at: user.subscription_expires_at,
    trial_expires_at: user.trial_expires_at,
    is_subscribed: user.is_subscribed,
    has_active_access: user.has_active_access,
    subscription_number: user.subscription_number,
    student_number: user.student_number,
    invoice_number: user.invoice_number,
    latest_payment_id: user.latest_payment_id,
    latest_payment: user.latest_payment || null,
    completed_tasks_count: user.completed_tasks_count,
    total_tasks_count: user.total_tasks_count,
    progress_percent: user.progress_percent,
    last_login_at: user.last_login_at,
    last_learning_activity_at: user.last_learning_activity_at,
    last_learning_activity_title: user.last_learning_activity_title,
    last_learning_task_id: user.last_learning_task_id,
    last_activity_at: user.last_activity_at,
    admin_mfa_enabled: user.admin_mfa_enabled,
    admin_mfa_required: user.admin_mfa_required,
    admin_mfa_setup_required: user.admin_mfa_setup_required,
    admin_mfa_verified: user.admin_mfa_verified,
    admin_mfa_verified_at: user.admin_mfa_verified_at,
    admin_mfa_verified_until: user.admin_mfa_verified_until,
  };

  if (remember) {
    localStorage.setItem("user", JSON.stringify(safeUser));
    sessionStorage.removeItem("user");
  } else {
    sessionStorage.setItem("user", JSON.stringify(safeUser));
    localStorage.removeItem("user");
  }
}

export function clearStoredUser() {
  localStorage.removeItem("user");
  sessionStorage.removeItem("user");
}

export function saveLastPosition({ pathId, dayId, taskId }) {
  localStorage.setItem(LAST_POS_KEY, JSON.stringify({ pathId, dayId, taskId }));
}

export function getLastPosition() {
  try {
    return JSON.parse(localStorage.getItem(LAST_POS_KEY) || "null");
  } catch {
    return null;
  }
}

export function clearLastPosition() {
  localStorage.removeItem(LAST_POS_KEY);
}

export function clearClientSession() {
  clearToken();
  clearStoredUser();
  clearLastPosition();
}

function parseDateSafe(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function computeLocalSubscriptionStatus(user) {
  const now = new Date();
  const status = user?.subscription_status;
  const until = parseDateSafe(user?.subscription_until);

  if (status === "active" && until && until > now) {
    return { status: "active", until };
  }

  if (status === "trial" && until && until > now) {
    return { status: "trial", until };
  }

  const subscriptionExpiresAt = parseDateSafe(user?.subscription_expires_at);
  if (subscriptionExpiresAt && subscriptionExpiresAt > now) {
    return { status: "active", until: subscriptionExpiresAt };
  }

  const trialExpiresAt = parseDateSafe(user?.trial_expires_at);
  if (trialExpiresAt && trialExpiresAt > now) {
    return { status: "trial", until: trialExpiresAt };
  }

  return { status: "none", until: null };
}

export function getStudentContentAccessState(rawUser = null) {
  const user = rawUser || getStoredUser();

  if (!user || user.role !== "student") {
    return {
      allowed: false,
      reason: "not_student",
      account_status: null,
      subscription_status: "none",
      subscription_until: null,
    };
  }

  const accountStatus = normalizeAccountStatus(user) || "registered";
  const subscription = computeLocalSubscriptionStatus(user);
  const subscriptionStatus = subscription.status;
  const subscriptionUntil = subscription.until
    ? subscription.until.toISOString()
    : null;

  if (accountStatus === "suspended") {
    return {
      allowed: false,
      reason: "suspended",
      account_status: accountStatus,
      subscription_status: subscriptionStatus,
      subscription_until: subscriptionUntil,
    };
  }

  if (accountStatus !== "active") {
    return {
      allowed: false,
      reason: "registered",
      account_status: accountStatus,
      subscription_status: subscriptionStatus,
      subscription_until: subscriptionUntil,
    };
  }

  if (subscriptionStatus === "active" || subscriptionStatus === "trial") {
    return {
      allowed: true,
      reason: "active",
      account_status: accountStatus,
      subscription_status: subscriptionStatus,
      subscription_until: subscriptionUntil,
    };
  }

  if (user?.trial_expires_at) {
    return {
      allowed: false,
      reason: "trial_expired",
      account_status: accountStatus,
      subscription_status: subscriptionStatus,
      subscription_until: subscriptionUntil,
    };
  }

  if (
    user?.subscription_status ||
    user?.subscription_until ||
    user?.subscription_expires_at
  ) {
    return {
      allowed: false,
      reason: "subscription_expired",
      account_status: accountStatus,
      subscription_status: subscriptionStatus,
      subscription_until: subscriptionUntil,
    };
  }

  return {
    allowed: false,
    reason: "not_subscribed",
    account_status: accountStatus,
    subscription_status: subscriptionStatus,
    subscription_until: subscriptionUntil,
  };
}

export function isAccessAllowed() {
  return getStudentContentAccessState().allowed;
}

export function getStudentLandingPath(rawUser = null) {
  const access = getStudentContentAccessState(rawUser);
  if (access.allowed) return "/student";
  if (access.reason === "suspended") return "/no-access";
  return "/student/subscription";
}

export function getStudentState() {
  const user = getStoredUser();
  if (!user) return null;

  const access = getStudentContentAccessState(user);

  return {
    account_status: access.account_status,
    subscription_status: access.subscription_status,
    subscription_until: access.subscription_until,
    progress_percent: user.progress_percent ?? 0,
    access_allowed: access.allowed,
    access_reason: access.reason,
  };
}

export async function getAccessToken() {
  return getToken();
}

export function hasClientSession() {
  return !!getToken() && !!getStoredUser();
}

export { getToken };
